import { useNavigate, useSearch } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import emailIcon from '../../../assets/email.svg'
import passIcon from '../../../assets/pass.svg'
import AuthRightPanel from '../../../components/auth/AuthRightPanel'
import OnboardingCard from '../../../components/common/OnboardingCard'
import ErrorPopup from '../../../components/common/errorPopup'
import { useSupabase } from '../../../contexts/SupabaseContext'
import { supabase } from '../../../hooks/supabase'

export default function SignIn() {
  const [showPassword, setShowPassword] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const { signInWithOAuth } = useSupabase()
  const navigate = useNavigate()
  const [isErrorPopupOpen, setIsErrorPopupOpen] = useState(false)
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const search = useSearch({ from: '/signin' })

  // Get return URL from search params
  const returnTo = search.returnTo || '/'

  useEffect(() => {
    const message = new URLSearchParams(window.location.search).get('message')
    if (message) {
      setToastMessage(message)
      setShowToast(true)
      // Clear the message from URL
      navigate({ to: '/signin', replace: true })
      // Hide toast after 5 seconds
      setTimeout(() => setShowToast(false), 5000)
    }
  }, [])

  useEffect(() => {
    if (search.error) {
      // Show business email restriction in popup
      if (search.error.includes('business email')) {
        setError(search.error)
        setIsErrorPopupOpen(true)
      } else {
        // Show other messages as toasts
        toast.info(search.error)
      }
    }
  }, [search.error])

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    
    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password
      })

      if (signInError) {
        // Show authentication errors in popup
        setError(signInError.message)
        setIsErrorPopupOpen(true)
        return
      }

      // Navigate to the return URL after successful sign in
      navigate({ to: returnTo as string, replace: true })
    } catch (error) {
      console.error('Sign in error:', error)
      // Show unexpected errors in popup
      setError(error instanceof Error ? error.message : 'Failed to sign in')
      setIsErrorPopupOpen(true)
    }
  }

  // Update OAuth sign in to handle return URL
  const handleOAuthSignIn = async (provider: 'google') => {
    try {
      await signInWithOAuth(provider, returnTo as string)
    } catch (error) {
      console.error('OAuth sign in error:', error)
      setError(error instanceof Error ? error.message : 'Failed to sign in')
      setIsErrorPopupOpen(true)
    }
  }

  const leftPanelContent = (
    <div className="flex flex-col h-full">
      <div className="flex flex-col p-[16px]">
        <div className="mb-1">
          <div className="text-[32px] font-semibold mb-8 text-left">Log in to your account</div>
          
          <div className="flex flex-col mb-6 border-b border-[#EEEFF1]">
            <button
              onClick={() => handleOAuthSignIn('google')}
              className="w-full flex items-center justify-center gap-2 font-inter rounded-[10px] bg-[#FBFBFB] px-4 py-2.5 text-sm font-normal text-[#222222] h-[40px] mb-6 shadow-[0px_0px_2px_0px_#E0E0E0]"
            >
              <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" className="w-[20px] h-[20px]" />
              Sign in with Google
            </button>
          </div>

          <form onSubmit={handleSignIn} className="flex flex-col">
            <div className="space-y-3">
              <div className="relative">
                <img 
                  src={emailIcon} 
                  alt="Email" 
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C5E63]"
                />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your work email"
                  className="w-full h-[40px] pl-10 pr-4 text-left rounded-[10px] text-[#222222] text-sm placeholder:text-[#9FA1A7] focus:outline-none focus:ring-1 focus:ring-[#29DC5B] focus:border-transparent shadow-[0px_0px_0px_1px_#EEEFF1_inset]"
                  required
                />
              </div>

              {search.message && (
                <p className="text-sm text-red-600 mt-1">
                  {search.message}
                </p>
              )}

              <div className="relative mb-7">
                <img 
                  src={passIcon} 
                  alt="Password" 
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C5E63]"
                />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  className="w-full h-[40px] pl-10 pr-4 text-left rounded-[10px] text-[#222222] text-sm placeholder:text-[#9FA1A7] focus:outline-none focus:ring-1 focus:ring-[#29DC5B] focus:border-transparent shadow-[0px_0px_0px_1px_#EEEFF1_inset]"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              className="w-full h-[40px] bg-[#29DC5B] mt-[28px] text-[#222222] rounded-[10px] flex items-center justify-center text-sm font-normal hover:bg-[#22c55e] transition-colors shadow-[0px_0px_0px_1px_#2325291A_inset]"
            >
              Sign in
            </button>
          </form>
        </div>

        <div className="text-center text-sm font-light text-[#9FA1A7] mt-[24px]">
          Need to create a new account? 
          <a href="/signup" className="text-[#222222] font-light ml-1 hover:underline">
            Sign up
          </a>
        </div>
      </div>

      <div className="mt-auto">
        <div className="text-center text-sm font-light text-[#9FA1A7]">
          Need help logging in? 
          <a href="/reset-password" className="text-[#222222] font-light ml-1 hover:underline">
            Reset your password
          </a>
        </div>
      </div>
    </div>
  )

  return (
    <div className="min-h-screen h-screen flex flex-col items-center">
      <OnboardingCard 
        leftPanel={leftPanelContent}
        rightPanel={<AuthRightPanel />}
      />

      {showToast && (
        <div className="fixed top-4 right-4 bg-[#FAFBFB] border border-[#29DC5B] text-[#29DC5B] px-4 py-3 rounded">
          <p className="text-sm">{toastMessage}</p>
        </div>
      )}

      <ErrorPopup
        isOpen={isErrorPopupOpen}
        onClose={() => setIsErrorPopupOpen(false)}
        message={error || 'An error occurred'}
      />
    </div>
  )
}
